import axios, { AxiosError } from 'axios';

import { cvUploadFilename } from '@/utils/fileUpload';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LoginErrorData = {
  status: 400 | 403 | 422;
  data?: {
    remaining_attempts?: 0 | 1 | 2 | 3 | 4;
    status?: number;
  };
};

export class LoginError extends Error {
  constructor(public readonly payload: LoginErrorData) {
    super('Login failed');
  }
}

// ---------------------------------------------------------------------------
// Captcha
// ---------------------------------------------------------------------------

export async function verifyCaptcha(token: string): Promise<void> {
  await axios.post('/api/auth/captcha', { token });
}

// ---------------------------------------------------------------------------
// SSO
// ---------------------------------------------------------------------------

export type SsoCheckResult = { sso: true; sso_url: string; slo_url: string; external_ref: string } | { sso: false };

export async function checkEmailSso(email: string): Promise<SsoCheckResult> {
  const response = await axios.get(`/api/auth/sso/${encodeURIComponent(email)}`);
  return response.data as SsoCheckResult;
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

export async function login(email: string, password: string): Promise<{ redirect: string }> {
  try {
    const response = await axios.post('/api/auth/login', { email, password });
    return { redirect: response.data.redirect ?? '/' };
  } catch (err) {
    if (err instanceof AxiosError && err.response) {
      throw new LoginError(err.response.data as LoginErrorData);
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Forgot password
// ---------------------------------------------------------------------------

export async function forgotPassword(email: string): Promise<void> {
  const response = await axios.post('/api/auth/forgot-password', { email });
  if (response.status >= 400) {
    throw new Error('forgot_password_failed');
  }
}

// ---------------------------------------------------------------------------
// Token check
// ---------------------------------------------------------------------------

export type TokenData = {
  valid: boolean;
  expired: boolean;
  params?: {
    email?: string;
    [key: string]: unknown;
  };
};

export async function checkToken(token: string): Promise<TokenData> {
  const response = await axios.get(`/api/auth/token/${token}`);
  const data = response.data;

  // Empty array = invalid token
  if (Array.isArray(data) && data.length === 0) {
    return { valid: false, expired: false };
  }

  if (data && data.expired_at) {
    const expired = new Date(data.expired_at).getTime() < Date.now();
    return {
      valid: !expired,
      expired,
      params: data.params
    };
  }

  return { valid: false, expired: false };
}

// ---------------------------------------------------------------------------
// Reset password
// ---------------------------------------------------------------------------

export async function resetPassword(token: string, password: string, passwordConfirmation: string): Promise<void> {
  await axios.patch(`/api/auth/reset-password/${token}`, {
    password,
    password_confirmation: passwordConfirmation
  });
}

// ---------------------------------------------------------------------------
// MFA
// ---------------------------------------------------------------------------

export type TotpSetupData = {
  qr_code_url: string;
  secret: string;
};

export async function mfaSetup(): Promise<TotpSetupData> {
  const response = await axios.post('/api/auth/mfa/setup');
  return response.data;
}

export async function mfaConfirmSetup(code: string): Promise<void> {
  await axios.post('/api/auth/mfa/confirm-setup', { code });
}

export async function mfaGenerateEmailCode(email: string): Promise<void> {
  await axios.post('/api/auth/mfa/email-code', { email });
}

export async function mfaChallenge(code: string): Promise<void> {
  await axios.post('/api/auth/mfa/challenge', { code });
}

// ---------------------------------------------------------------------------
// Spaces / Me
// ---------------------------------------------------------------------------

export type Space = {
  uuid: string;
  company: {
    uuid: string;
    label: string;
    logo?: string | null;
  };
};

export type MeSpaces = {
  admin: {
    is_admin: boolean;
    company: { uuid: string; label: string; logo?: string | null };
  };
  recruiter: Space[];
  manager: Space[];
};

export type MeUser = {
  id?: number;
  uuid: string;
  email: string;
  first_name?: string;
  last_name?: string;
  locale_id?: number;
  locale?: string;
  locale_label?: string;
  is_2FA_enabled?: boolean;
  cv_upload_status?: string; // 'completed' | 'pending' | null
  gender?: string; // 'm' | 'f' | null — used by OnboardingPronounsView
  job_title_id?: number | null; // used by OnboardingLoadingView
  job_title?: string | null; // used by OnboardingLoadingView
  custom_job?: string | null; // used by OnboardingLoadingView
  company_uuid?: string;
  customer_uuid?: string;
  disc_id?: string | null; // personality code — drives the avatar gradient
  // The user's own branding, distinct from company.branding below.
  // `id` is the avatar file name on the assets CDN.
  branding?: {
    id?: string | null;
    avatar?: string | null;
    cover?: string | null;
    gradients?: string[];
  } | null;
  company?: {
    branding?: import('@/utils/branding').CompanyBranding;
  };
};

export async function getSpaces(): Promise<MeSpaces> {
  const response = await axios.get('/api/spaces');
  return response.data;
}

export async function getMe(): Promise<MeUser> {
  const response = await axios.get('/api/me');
  // Viktor returns { user: {...}, is_2FA_enabled: bool, permissions: [...], company_uuid: "..." }
  // company_uuid and is_2FA_enabled are at root level, not inside user — merge both
  const user = response.data.user ?? response.data;
  return {
    ...user,
    company_uuid: response.data.company_uuid ?? user.company_uuid,
    is_2FA_enabled: response.data.is_2FA_enabled ?? user.is_2FA_enabled
  };
}

export async function updateUserLocale(localeId: number): Promise<void> {
  await axios.patch('/api/user/locale', { locale_id: localeId });
}

export async function getMeWithPermissions(): Promise<{ user: MeUser; permissions: string[] }> {
  const response = await axios.get('/api/me');
  const user = response.data.user ?? response.data;
  return {
    user: { ...user, is_2FA_enabled: response.data.is_2FA_enabled ?? user.is_2FA_enabled },
    permissions: response.data.permissions ?? []
  };
}

// ---------------------------------------------------------------------------
// Terms of use
// ---------------------------------------------------------------------------

export async function acceptTermsOfUse(): Promise<void> {
  await axios.patch('/api/terms-of-use');
}

export async function getUserFlow(): Promise<{ status: string; redirect: string }> {
  const response = await axios.get('/api/user-flow');
  return response.data;
}

export async function confirmInvitations(): Promise<{ redirect: string; is_recruiter?: boolean }> {
  const response = await axios.post('/api/invitations/confirm');
  return response.data;
}

export async function selectSpace(
  company: string,
  role: 'admin' | 'recruiter' | 'manager',
  user: string
): Promise<string> {
  const response = await axios.post('/api/spaces/select', { company, role, user });
  return response.data.redirect as string;
}

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

export type InvitationAction = 'accepted' | 'refused';

export type RoleInvitationsData = {
  admin: any[];
  recruiter: any[];
  manager: any[];
};

export type ShareInvitationsData = {
  candidate: any[];
  network: any[];
};

export async function getRoleInvitations(): Promise<RoleInvitationsData> {
  const response = await axios.get('/api/invitations/role');
  return response.data;
}

export async function getShareProfileInvitations(): Promise<ShareInvitationsData> {
  const response = await axios.get('/api/invitations/share-profile');
  return response.data;
}

export async function updateInvitation(
  role: string,
  entity_uuid: string,
  user_uuid: string,
  action: InvitationAction
): Promise<void> {
  await axios.patch('/api/invitations', { role, entity_uuid, user_uuid, action });
}

export async function uploadCv(file: File): Promise<void> {
  const formData = new FormData();
  formData.append('cv', file, cvUploadFilename(file));
  await axios.post('/api/invitations/cv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
}
